import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiShield, FiSmartphone } from 'react-icons/fi';
import favicon from '../assets/favicon.png';
import { dialerCrmRequest, getPendingOtp, savePendingOtp, requestError, SESSION_KEY, PENDING_KEY } from '../utils/dialerCrmApi';
import './MobileOTP.scss';

export default function MobileOTP() {
    const [pending, setPending] = useState(getPendingOtp);
    const [digits, setDigits] = useState(['', '', '', '']);
    const inputs = useRef([]);
    const [busy, setBusy] = useState('');
    const [now, setNow] = useState(Date.now());
    const [validated, setValidated] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!pending) { navigate('/', { replace: true }); return undefined; }
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [pending, navigate]);

    if (!pending) return null;
    const seconds = Math.max(0, Math.ceil((pending.resendAt - now) / 1000));
    const expired = now >= pending.expiresAt;

    const enterDigits = (value, index) => {
        const numbers = value.replace(/\D/g, '').slice(0, 4);
        const next = [...digits];
        const start = numbers.length === 4 ? 0 : index;
        if (!numbers) next[index] = '';
        else numbers.split('').forEach((digit, offset) => {
            if (start + offset < 4) next[start + offset] = digit;
        });
        setDigits(next);
        if (numbers) inputs.current[Math.min(start + numbers.length, 3)]?.focus();
    };

    const handleKey = (event, index) => {
        if (event.key === 'Backspace' && !digits[index] && index > 0) {
            event.preventDefault();
            inputs.current[index - 1]?.focus();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            inputs.current[Math.max(0, Math.min(3, index + (event.key === 'ArrowLeft' ? -1 : 1)))]?.focus();
        }
    };

    const submit = async event => {
        event.preventDefault();
        setValidated(true);
        if (busy || expired || !event.currentTarget.checkValidity()) return;
        setBusy('verify');
        try {
            const response = await dialerCrmRequest('verify-otp', { mobile: pending.mobile, otp: digits.join(''), challenge: pending.challenge });
            if (response.status !== 1) { toast.error(response.msg); return; }
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(response.data));
            sessionStorage.removeItem(PENDING_KEY);
            toast.success(response.msg);
            navigate('/dashboard', { replace: true });
        } catch (error) {
            toast.error(requestError(error));
        } finally {
            setBusy('');
        }
    };

    const resend = async () => {
        if (busy || seconds > 0) return;
        setBusy('resend');
        try {
            const response = await dialerCrmRequest('resend-otp', { mobile: pending.mobile });
            if (response.status !== 1) { toast.error(response.msg); return; }
            savePendingOtp(pending.mobile, response.data);
            setPending(getPendingOtp());
            setDigits(['', '', '', '']);
            setValidated(false);
            setNow(Date.now());
            toast.success(response.msg);
        } catch (error) {
            toast.error(requestError(error));
        } finally {
            setBusy('');
        }
    };

    return <div className="homePage dialer-otp-page">
        <Helmet><title>Satkartar | Verify Login OTP</title></Helmet>
        <div className="auth-shell"><div className="user_card otp_card"><div className="auth-card-inner">
            <div className="auth-header">
                <div className="brand_logo_container"><img src={favicon} className="brand_logo" alt="Satkartar logo" /></div>
                <h1 className="loginnames">VERIFY OTP</h1>
                <p className="auth-subtitle">LEAD PUSH DIALER WITH CRM</p>
                <span className="auth-pill"><FiSmartphone /> OTP sent to +91 &bull;&bull;&bull;&bull;&bull;&bull;{pending.mobile.slice(-4)}</span>
            </div>
            <div className="form_container otp_form_container">
                <Form noValidate validated={validated} onSubmit={submit}>
                    <Form.Group className="mobileinput otpinput">
                        <Form.Label id="otp-label" htmlFor="otp-digit-0"><FiShield /> ENTER 4-DIGIT OTP</Form.Label>
                        <div className="otp-boxes" role="group" aria-labelledby="otp-label">
                            {digits.map((digit, index) => <Form.Control key={index}
                                id={`otp-digit-${index}`} ref={element => { inputs.current[index] = element; }}
                                required type="text" inputMode="numeric" autoComplete={index === 0 ? 'one-time-code' : 'off'}
                                autoFocus={index === 0} className="otp-box" name={`otp-digit-${index}`}
                                value={digit} pattern="[0-9]" maxLength={4} aria-label={`OTP digit ${index + 1}`}
                                onChange={event => enterDigits(event.target.value, index)}
                                onKeyDown={event => handleKey(event, index)} onFocus={event => event.target.select()}
                                onPaste={event => {
                                    event.preventDefault();
                                    enterDigits(event.clipboardData.getData('text'), index);
                                }} disabled={!!busy} />)}
                        </div>
                        <Form.Control.Feedback type="invalid">Please enter the 4-digit OTP.</Form.Control.Feedback>
                    </Form.Group>
                    {expired && <p role="alert">OTP expired. Please request a new OTP.</p>}
                    <button type="button" className="reSendMobileOTP" onClick={resend} disabled={!!busy || seconds > 0}>
                        {busy === 'resend' ? 'Sending...' : seconds > 0 ? `Resend OTP in ${seconds}s` : 'Resend OTP'}
                    </button>
                    <div className="login_container"><button type="submit" className="btn login_btn" disabled={!!busy || expired}>
                        {busy === 'verify' ? 'VERIFYING...' : 'VERIFY & LOGIN'}
                    </button></div>
                    <button type="button" className="back-login" disabled={!!busy} onClick={() => {
                        sessionStorage.removeItem(PENDING_KEY); navigate('/', { replace: true });
                    }}><FiArrowLeft /> Back to Login</button>
                </Form>
            </div>
            <p className="auth-footer">&copy; 2026 Sat Kartar Life Limited.</p>
        </div></div></div>
    </div>;
}
